// add-check-constraint / drop-check-constraint edit planner (v2 Slice B4).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B4.
//
// Both ops append a fresh ALTER TABLE statement after the source statement
// (DSE2-7: no coalescing by default; opt-in coalescing is slice H1). Source
// path may be either ALTER TABLE or CREATE TABLE — the synthesized statement
// targets the same table named by the source structure.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAddCheckConstraint,
  synthesizeDropCheckConstraint,
} from './dialect.check';

type CheckConstraintEdit = Extract<
  DdlEdit,
  { op: 'add-check-constraint' | 'drop-check-constraint' }
>;

export function planCheckConstraintEdit(
  ctx: DdlEditCtx,
  edit: CheckConstraintEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  if (!table) {
    throw new Error(
      `applyDdlEdit: cannot apply ${edit.op} edit to ${ctx.structure.kind} statement`,
    );
  }
  const synth = edit.op === 'add-check-constraint'
    ? synthesizeAddCheckConstraint(ctx.engine, table, edit.check)
    : synthesizeDropCheckConstraint(ctx.engine, table, edit.constraintName, edit.cascade);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:alter-table`,
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}
